import React from 'react';
import { Palette, User, Users, Trophy, Clock, MessageCircle, Globe, Award, ShieldCheck, EyeOff, Flag, Lock, Heart } from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const kidsItems = [
  { icon: Palette, title: 'Creative Tools', desc: 'Post photos, videos, stories, and art in a safe creative studio.', color: 'from-fuchsia-500 to-pink-600' },
  { icon: User, title: 'Profile Customization', desc: 'Design your avatar, choose auras, frames, and express your identity.', color: 'from-violet-500 to-purple-600' },
  { icon: Users, title: 'Interest-Based Clubs', desc: 'Join communities around your passions — art, space, music, coding.', color: 'from-indigo-500 to-violet-600' },
  { icon: Trophy, title: 'Challenges', desc: 'Participate in creative community challenges and earn recognition.', color: 'from-amber-500 to-orange-600' },
  { icon: Clock, title: 'Times Cards', desc: 'Collect and share limited-time gift cards with your friends.', color: 'from-fuchsia-600 to-rose-600' },
  { icon: MessageCircle, title: 'Safe Messenger', desc: 'Chat with friends knowing RONI is there to keep things safe.', color: 'from-cyan-500 to-blue-600' },
  { icon: Globe, title: 'International Friends', desc: 'Connect with peers from other countries through shared interests.', color: 'from-emerald-500 to-teal-600' },
  { icon: Award, title: 'Recognition Path', desc: 'Build your creative journey and earn recognition for expression.', color: 'from-rose-500 to-pink-600' },
];

const parentsItems = [
  { icon: User, title: 'Age-Aware Design', desc: 'Every feature calibrated for the 9–14 developmental stage — not adult social mechanics.', color: 'from-emerald-500 to-teal-600' },
  { icon: MessageCircle, title: 'Safer Messaging', desc: 'Friend-based messaging with built-in safety signals and RONI guidance.', color: 'from-cyan-500 to-blue-600' },
  { icon: Flag, title: 'Reporting & Blocking', desc: 'Accessible reporting and blocking on every piece of content and every conversation.', color: 'from-violet-500 to-purple-600' },
  { icon: ShieldCheck, title: 'RONI AI Safety', desc: 'AI-assisted safety companion that helps detect unsafe patterns in real time.', color: 'from-emerald-500 to-cyan-600' },
  { icon: Lock, title: 'Privacy-Conscious Profiles', desc: 'Creative identity without exposing real name, age, or sensitive personal data.', color: 'from-purple-500 to-fuchsia-600' },
  { icon: EyeOff, title: 'No Location Exposure', desc: 'No precise location data is collected or displayed in child profiles.', color: 'from-slate-500 to-gray-600' },
  { icon: Heart, title: 'No Negative Mechanics', desc: 'No dislike buttons, no harmful comparison metrics, no toxic reaction culture.', color: 'from-rose-500 to-pink-600' },
];

const WhySection = () => (
  <>
    {/* Kids Section */}
    <section data-testid="why-kids-section" className="relative py-24 bg-[#080714] overflow-hidden">
      <div className="absolute top-1/3 right-0 w-72 h-72 bg-fuchsia-600/10 rounded-full blur-[120px] pointer-events-none"/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-fuchsia-400 mb-3 font-outfit">For Kids</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white tracking-tight mb-3">Why kids will love it.</h2>
          <p className="text-white/50 text-base mb-12 max-w-xl">
            A creative universe designed to excite, inspire, and connect — without the chaos and pressure of adult social networks.
          </p>
        </FadeIn>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {kidsItems.map((item, i) => (
            <FadeIn key={item.title} delay={i * 50}>
              <div data-testid={`kids-why-${i}`} className="glass-card p-5 h-full hover:border-fuchsia-500/25 transition-all">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg`}>
                  <item.icon size={18} className="text-white"/>
                </div>
                <h3 className="font-outfit font-semibold text-white text-sm mb-1.5">{item.title}</h3>
                <p className="text-white/42 text-xs leading-relaxed">{item.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* Parents Section */}
    <section data-testid="why-parents-section" className="relative py-24 overflow-hidden">
      <div className="absolute top-1/3 left-0 w-72 h-72 bg-emerald-600/8 rounded-full blur-[120px] pointer-events-none"/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-emerald-400 mb-3 font-outfit">For Parents</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white tracking-tight mb-3">Why parents can trust it.</h2>
          <p className="text-white/50 text-base mb-12 max-w-xl">
            Every product decision is evaluated against one question: is this safe and appropriate for a child aged 9–14?
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {parentsItems.map((item, i) => (
            <FadeIn key={item.title} delay={i * 50}>
              <div data-testid={`parents-why-${i}`} className={`glass-card p-5 h-full hover:border-emerald-500/20 transition-all ${i === 3 ? 'lg:col-span-1' : ''}`}>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg`}>
                  <item.icon size={18} className="text-white"/>
                </div>
                <h3 className="font-outfit font-semibold text-white text-sm mb-1.5">{item.title}</h3>
                <p className="text-white/42 text-xs leading-relaxed">{item.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Conceptual design pillars */}
        <FadeIn delay={200} className="mt-10">
          <div className="glass-card p-6 sm:p-8">
            <p className="text-xs font-outfit font-semibold text-white/40 uppercase tracking-widest mb-4">Product Design Pillars</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {[
                { label: 'Creativity', color: 'bg-fuchsia-500/20 border-fuchsia-500/30 text-fuchsia-400' },
                { label: 'Safety', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
                { label: 'Communication', color: 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400' },
                { label: 'Interests', color: 'bg-violet-500/20 border-violet-500/30 text-violet-400' },
                { label: 'Recognition', color: 'bg-amber-500/20 border-amber-500/30 text-amber-400' },
                { label: 'Parent Trust', color: 'bg-purple-500/20 border-purple-500/30 text-purple-400' },
              ].map(p => (
                <div key={p.label} className={`border rounded-xl py-3 px-2 text-center text-xs font-outfit font-semibold ${p.color}`}>
                  {p.label}
                </div>
              ))}
            </div>
            <p className="text-white/25 text-xs mt-4">These reflect the platform's design direction — not performance metrics or user data.</p>
          </div>
        </FadeIn>
      </div>
    </section>
  </>
);

export default WhySection;
